
import { useEffect, useState } from "react";
import { actualizarFotoPerfil, getAllPersonas, getOnePersona } from "../../../services/persona.service";
import { ContenedorPageTemplate } from "../../../components/templates/ContenedorPageTemplate";
import { getOneDepartamento } from "../../../services/departamento.service";
import { getOneProvincia } from "../../../services/provincia.service";
import { getOnePais } from "../../../services/pais.service";
import { useNavigate } from "react-router-dom";
import { showErrorAlert } from "../../../components/molecules/ShowAlert";
import { BASE_URL } from "../../../utils/configAxios";
import { getImagePerfil } from "../../../services/image.service";

const obtenerUsuarioLocal = () => {
    try {
        const user = JSON.parse(localStorage.getItem("userInfoES") || "{}");
        return user?.id ? user : null;
    } catch {
        return null;
    }
};

const buildAbsoluteUrl = (ruta) => {
    if (!ruta) return null;
    if (/^https?:\/\//i.test(ruta)) return ruta;
    return `${BASE_URL}/${ruta.replace(/^\/+/, "")}`;
};

export const PerfilDocentePage = () => {
    const [perfil, setPerfil] = useState(null);
    const navigate = useNavigate();

    useEffect(() => {
        const fetchPerfil = async () => {
            const usuario = obtenerUsuarioLocal();
            if (!usuario) {
                return;
            }

            try {
                const personas = await getAllPersonas();
                const persona = personas.find((p) => p.usuarioid === usuario.id);

                if (!persona) {
                    return;
                }

                let departamento = null;
                let provincia = null;
                let pais = null;

                if (persona.idDepartamento) {
                    departamento = await getOneDepartamento(persona.idDepartamento);
                }
                if (persona.idProvincia) {
                    provincia = await getOneProvincia(persona.idProvincia);
                }
                if (persona.idPais) {
                    pais = await getOnePais(persona.idPais);
                }

                let imagenUrl = null;
                if (persona.imagen && persona.imagen.trim() !== "" && persona.imagen !== "null") {
                    const partes = persona.imagen.split("/");
                    const fileName = partes[partes.length - 1];
                    try {
                        imagenUrl = await getImagePerfil(fileName);
                    } catch {
                        imagenUrl = buildAbsoluteUrl(persona.imagen);
                    }
                }

                const perfilFormateado = {
                    idPersona: persona.idPersona,
                    nombre: persona.nombre,
                    apellido: persona.apellido,
                    username: usuario.username,
                    telefono: persona.celular,
                    ci: persona.ci || "No registrado",
                    genero: persona.sexo || "No registrado",
                    correo: persona.correo || "No registrado",
                    fechaNacimiento: persona.fechaNacimiento?.split("T")[0],
                    pais: pais?.nombre || "Bolivia",
                    departamento: departamento?.nombre || "No registrado",
                    provincia: provincia?.nombre || "No registrado",
                    imagen: imagenUrl,
                };

                setPerfil(perfilFormateado);
            } catch (error) {
                console.error("Error al obtener el perfil del administrador:", error);
            }
        };

        fetchPerfil();
    }, []);

    if (!perfil) {
        return (
            <ContenedorPageTemplate titulo="Mi Perfil">
                <p className="text-center text-gray-600">Cargando datos personales...</p>
            </ContenedorPageTemplate>
        );
    }

    // Iconos para cada tipo de dato
    const getIcon = (label) => {
        const iconMap = {
            "Cédula de Identidad": "🆔",
            "Fecha de Nacimiento": "📅",
            "Género": "👤",
            "Correo electrónico": "✉️",
            "Teléfono": "📱",
            "País": "🌎",
            "Departamento": "📍",
            "Provincia": "🗺️",
        };
        return iconMap[label] || "📋";
    };

    const PerfilDato = ({ label, valor }) => {
        return (
            <div className="group relative p-5 rounded-xl shadow-md transition-all duration-300 hover:shadow-xl hover:-translate-y-1 bg-white border-2 border-gray-100 hover:border-theme-blue/30">
                <div className="flex items-start gap-3">
                    <div className="text-2xl flex-shrink-0 bg-theme-blue/10 p-2 rounded-lg">
                        {getIcon(label)}
                    </div>
                    <div className="flex-1 min-w-0">
                        <p className="text-xs font-semibold uppercase tracking-wide mb-2 text-gray-500">
                            {label}
                        </p>
                        <p className="text-base font-bold break-words text-gray-800">
                            {valor}
                        </p>
                    </div>
                </div>
                <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-transparent via-white/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"></div>
            </div>
        );
    };

    return (
        <div className="bg-gradient-to-br from-blue-50 via-white to-yellow-50 min-h-screen">
            <ContenedorPageTemplate fondo={false}>
                {/* Header con gradiente */}
                <div className="max-w-6xl mx-auto mt-8 mb-8">
                    <div className="bg-gradient-to-r from-theme-blue via-blue-600 to-theme-yellow rounded-2xl shadow-2xl overflow-hidden">
                        <div className="bg-white/95 backdrop-blur-sm p-8 md:p-12">
                            <div className="flex flex-col md:flex-row items-center gap-8">
                                {/* Avatar grande */}
                                <div className="relative w-32 h-32 md:w-40 md:h-40 group flex-shrink-0">
                                    <div className="absolute inset-0 bg-gradient-to-br from-theme-blue to-theme-yellow rounded-full p-1">
                                        <div className="w-full h-full bg-white rounded-full p-1">
                                            <img
                                                src={
                                                    perfil.imagen
                                                        ? perfil.imagen
                                                        : "https://cdn-icons-png.flaticon.com/512/847/847969.png"
                                                }
                                                alt="Foto de perfil"
                                                className="w-full h-full rounded-full object-cover"
                                            />
                                        </div>
                                    </div>

                                    {/* Hover + ícono */}
                                    <label
                                        htmlFor="uploadFotoDocente"
                                        className="absolute inset-0 bg-gradient-to-br from-theme-blue/80 to-theme-yellow/80 rounded-full opacity-0 group-hover:opacity-100 transition-all duration-300 flex items-center justify-center cursor-pointer backdrop-blur-sm"
                                        title="Cambiar foto"
                                    >
                                        <div className="bg-white rounded-full p-3 shadow-lg">
                                            <svg className="w-6 h-6 text-theme-blue" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                                            </svg>
                                        </div>
                                    </label>

                                    <input
                                        type="file"
                                        id="uploadFotoDocente"
                                        accept="image/*"
                                        className="hidden"
                                        onChange={async (e) => {
                                            const file = e.target.files[0];
                                            if (file) {
                                                const validExtensions = ["image/jpeg", "image/png", "image/jpg"];
                                                const maxSize = 5 * 1024 * 1024; // 5MB

                                                if (!validExtensions.includes(file.type)) {
                                                    showErrorAlert({
                                                        title: "Formato inválido",
                                                        text: "Solo se permiten imágenes JPG, JPEG o PNG.",
                                                    });
                                                    return;
                                                }

                                                if (file.size > maxSize) {
                                                    showErrorAlert({
                                                        title: "Tamaño excedido",
                                                        text: "La imagen no debe superar los 5MB.",
                                                    });
                                                    return;
                                                }

                                                try {
                                                    const formData = new FormData();
                                                    formData.append("IdPersona", perfil.idPersona);
                                                    formData.append("Foto", file);
                                                    await actualizarFotoPerfil(formData);

                                                    try {
                                                        await new Promise(resolve => setTimeout(resolve, 500));
                                                        const personaActualizada = await getOnePersona(perfil.idPersona);
                                                        if (personaActualizada?.imagen) {
                                                            const partes = personaActualizada.imagen.split("/");
                                                            const fileName = partes[partes.length - 1];
                                                            const updatedUrl = await getImagePerfil(fileName);
                                                            setPerfil((prev) => ({ ...prev, imagen: updatedUrl }));
                                                        } else {
                                                            const updatedUrl = URL.createObjectURL(file);
                                                            setPerfil((prev) => ({ ...prev, imagen: updatedUrl }));
                                                        }
                                                    } catch (err) {
                                                        const updatedUrl = URL.createObjectURL(file);
                                                        setPerfil((prev) => ({ ...prev, imagen: updatedUrl }));
                                                    }
                                                } catch (error) {
                                                    console.error("Error al subir la imagen:", error);
                                                    showErrorAlert({
                                                        title: "Error al subir imagen",
                                                        text: "No se pudo guardar la imagen. Intenta más tarde.",
                                                    });
                                                }
                                            }
                                        }}
                                    />
                                </div>

                                {/* Nombre y correo */}
                                <div className="flex-1 text-center md:text-left">
                                    <h1 className="text-3xl md:text-4xl font-black text-theme-blue mb-2">
                                        {perfil.nombre} {perfil.apellido}
                                    </h1>
                                    <div className="inline-flex items-center gap-2 bg-theme-yellow/20 px-4 py-2 rounded-full mb-4">
                                        <span className="text-theme-blue font-bold">Docente</span>
                                        <span className="text-theme-blue/70">•</span>
                                        <span className="text-theme-blue font-semibold">{perfil.username}</span>
                                    </div>
                                    <button
                                        onClick={() => navigate("/perfilDocente/editarPerfilDocente")}
                                        className="bg-gradient-to-r from-theme-blue to-blue-600 hover:from-blue-700 hover:to-blue-800 text-white px-6 py-3 rounded-xl shadow-lg font-semibold transition-all duration-300 hover:shadow-xl hover:scale-105 flex items-center gap-2 mx-auto md:mx-0"
                                    >
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                        </svg>
                                        Actualizar perfil
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Contenedor de datos */}
                <div className="max-w-6xl mx-auto bg-white rounded-2xl shadow-xl p-6 md:p-8 mb-8">
                    {/* Título de sección */}
                    <div className="mb-6">
                        <h2 className="text-2xl font-bold text-theme-blue mb-2 flex items-center gap-2">
                            <span className="bg-gradient-to-r from-theme-blue to-theme-yellow w-1 h-8 rounded-full"></span>
                            Información Personal
                        </h2>
                        <p className="text-gray-600 text-sm">Gestiona y actualiza tu información personal</p>
                    </div>

                    {/* Datos en grid estilo tarjeta */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
                        <PerfilDato label="Cédula de Identidad" valor={perfil.ci} />
                        <PerfilDato label="Fecha de Nacimiento" valor={perfil.fechaNacimiento} />
                        <PerfilDato label="Género" valor={perfil.genero} />
                        <PerfilDato label="Correo electrónico" valor={perfil.correo} />
                        <PerfilDato label="Teléfono" valor={perfil.telefono} />
                    </div>

                    {/* Sección de ubicación */}
                    <div className="mt-8 pt-8 border-t-2 border-gray-100">
                        <h2 className="text-2xl font-bold text-theme-blue mb-6 flex items-center gap-2">
                            <span className="bg-gradient-to-r from-theme-blue to-theme-yellow w-1 h-8 rounded-full"></span>
                            Ubicación
                        </h2>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 md:gap-6">
                            <PerfilDato label="País" valor={perfil.pais} />
                            <PerfilDato label="Departamento" valor={perfil.departamento} />
                            <PerfilDato label="Provincia" valor={perfil.provincia} />
                        </div>
                    </div>
                </div>
            </ContenedorPageTemplate>
        </div>
    );
}